import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// Process Stage Types
export interface ProcessStageDto {
  id: number;
  clientId: number;
  statusId: number; // FK to Status table
  statusColor: string; // Color from linked status
  stageName: string;
  description?: string;
  stageDate?: string;
  status: number;
  statusName: string;
  sortOrder: number;
  isSystemStage: boolean;
  icon?: string;
  createdAt: string;
}

export interface CreateProcessStageDto {
  clientId: number;
  stageName: string;
  description?: string;
  stageDate?: string;
  status: number;
  sortOrder: number;
  icon?: string;
}

export interface UpdateProcessStageDto {
  stageName?: string;
  description?: string;
  stageDate?: string;
  status?: number;
  sortOrder?: number;
  icon?: string;
}

export interface UpdateStageStatusDto {
  status: number; // 1=Pending, 2=Current, 3=Completed
  stageDate?: string;
  description?: string;
}

// Committee Types
export interface CommitteeDto {
  id: number;
  clientId: number;
  clientName: string;
  committeeDate: string;
  location?: string;
  escortNeeded: boolean;
  escortType?: string;
  notes?: string;
  usesLawyer: boolean;
  lawyerName?: string;
  requiresMedicalOpinion: boolean;
  medicalOpinionDetails?: string;
  decisionStatus: number;
  decisionStatusName: string;
  disabilityPercentage?: number;
  periodType?: number; // deprecated
  periodTypeName?: string; // deprecated
  approvalEndDate?: string; // תאריך סיום זכאות (עבור ApprovedForPeriod)
  monthlyBenefitAmount?: number;
  retroactiveFromDate?: string;
  retroactiveTotalAmount?: number;
  decisionDocumentPath?: string;
  decisionNotes?: string;
  evaluationNumber: number; // מספר הערכה (1, 2, או 3)
  reminderMessage?: string; // הודעה על תזכורת שנוצרה
  createdBy: number;
  createdByName: string;
  createdAt: string;
  updatedBy?: number;
  updatedByName?: string;
  updatedAt?: string;
}

export interface CreateCommitteeDto {
  clientId: number;
  committeeDate: string;
  location?: string;
  escortNeeded: boolean;
  escortType?: string;
  notes?: string;
  usesLawyer: boolean;
  lawyerName?: string;
  requiresMedicalOpinion: boolean;
  medicalOpinionDetails?: string;
}

export interface UpdateCommitteeDecisionDto {
  decisionStatus: number;
  disabilityPercentage?: number;
  periodType?: number; // deprecated
  approvalEndDate?: string; // תאריך סיום זכאות (עבור ApprovedForPeriod)
  monthlyBenefitAmount?: number;
  retroactiveFromDate?: string;
  retroactiveTotalAmount?: number;
  decisionDocumentPath?: string;
  decisionNotes?: string;
}

// Enums
export enum ProcessStageStatus {
  Pending = 1,
  Current = 2,
  Completed = 3
}

export enum CommitteeDecisionStatus {
  Pending = 1,
  ApprovedPermanent = 2, // אושר לצמיתות
  Rejected = 3,
  ApprovedForPeriod = 4  // אושר לתקופה (עם תאריך סיום)
}

export enum BenefitPeriodType {
  OneYear = 1,
  TwoYears = 2,
  Permanent = 3
}

// API calls - Process Stages
export const processStagesApi = {
  getById: async (id: number): Promise<ProcessStageDto> => {
    const response = await axios.get(`${API_BASE_URL}/processstages/${id}`);
    return response.data;
  },

  getByClientId: async (clientId: number): Promise<ProcessStageDto[]> => {
    const response = await axios.get(`${API_BASE_URL}/processstages/client/${clientId}`);
    return response.data;
  },

  create: async (dto: CreateProcessStageDto): Promise<ProcessStageDto> => {
    const response = await axios.post(`${API_BASE_URL}/processstages`, dto);
    return response.data;
  },

  update: async (id: number, dto: UpdateProcessStageDto): Promise<ProcessStageDto> => {
    const response = await axios.put(`${API_BASE_URL}/processstages/${id}`, dto);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/processstages/${id}`);
  },

  initializeClientStages: async (clientId: number): Promise<void> => {
    await axios.post(`${API_BASE_URL}/processstages/client/${clientId}/initialize`);
  },

  updateStatus: async (id: number, dto: UpdateStageStatusDto): Promise<ProcessStageDto> => {
    const response = await axios.put(`${API_BASE_URL}/processstages/${id}/status`, dto);
    return response.data;
  },

  reinitialize: async (clientId: number): Promise<void> => {
    await axios.post(`${API_BASE_URL}/processstages/client/${clientId}/reinitialize`);
  },

  syncClientStages: async (clientId: number): Promise<{ message: string; stages: ProcessStageDto[] }> => {
    const response = await axios.post(`${API_BASE_URL}/processstages/client/${clientId}/sync`);
    return response.data;
  }
};

// API calls - Committees
export const committeesApi = {
  getById: async (id: number): Promise<CommitteeDto> => {
    const response = await axios.get(`${API_BASE_URL}/committees/${id}`);
    return response.data;
  },

  getByClientId: async (clientId: number): Promise<CommitteeDto[]> => {
    const response = await axios.get(`${API_BASE_URL}/committees/client/${clientId}`);
    return response.data;
  },

  getLatestByClientId: async (clientId: number): Promise<CommitteeDto | null> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/committees/client/${clientId}/latest`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  create: async (dto: CreateCommitteeDto): Promise<CommitteeDto> => {
    const response = await axios.post(`${API_BASE_URL}/committees`, dto);
    return response.data;
  },

  updateDecision: async (id: number, dto: UpdateCommitteeDecisionDto): Promise<CommitteeDto> => {
    const response = await axios.put(`${API_BASE_URL}/committees/${id}/decision`, dto);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/committees/${id}`);
  }
};

// Helper functions
export const getStageStatusColor = (status: ProcessStageStatus): string => {
  switch (status) {
    case ProcessStageStatus.Completed:
      return 'var(--success-color)';
    case ProcessStageStatus.Current:
      return 'var(--primary-color)';
    case ProcessStageStatus.Pending:
      return 'var(--text-secondary)';
    default:
      return 'var(--text-secondary)';
  }
};

export const getDecisionStatusColor = (status: CommitteeDecisionStatus): string => {
  switch (status) {
    case CommitteeDecisionStatus.ApprovedPermanent:
    case CommitteeDecisionStatus.ApprovedForPeriod:
      return 'var(--success-color)';
    case CommitteeDecisionStatus.Rejected:
      return 'var(--error-color)';
    case CommitteeDecisionStatus.Pending:
      return 'var(--warning-color)';
    default:
      return 'var(--text-secondary)';
  }
};

// Helper to check if decision is approved (either permanent or for period)
export const isApproved = (status: CommitteeDecisionStatus): boolean => {
  return status === CommitteeDecisionStatus.ApprovedPermanent ||
         status === CommitteeDecisionStatus.ApprovedForPeriod;
};

export const getDecisionStatusName = (status: CommitteeDecisionStatus): string => {
  switch (status) {
    case CommitteeDecisionStatus.Pending:
      return 'ממתין להחלטה';
    case CommitteeDecisionStatus.ApprovedPermanent:
      return 'אושר לצמיתות';
    case CommitteeDecisionStatus.ApprovedForPeriod:
      return 'אושר לתקופה';
    case CommitteeDecisionStatus.Rejected:
      return 'נדחה';
    default:
      return 'לא ידוע';
  }
};

export const getPeriodTypeName = (periodType?: number): string => {
  if (!periodType) return '-';
  switch (periodType) {
    case BenefitPeriodType.OneYear:
      return 'שנה';
    case BenefitPeriodType.TwoYears:
      return 'שנתיים';
    case BenefitPeriodType.Permanent:
      return 'קבוע';
    default:
      return '-';
  }
};

export const formatCurrency = (amount?: number): string => {
  if (!amount) return '-';
  return `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// טיפול אחיד בשגיאות API — מקור אמת מרכזי
export { handleApiError } from '../utils/errorHandler';
